import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import type { RootState } from '../index';
import type { BlogsState } from '../types';
import type { BlogListParams } from '../../types/api.types';
import {
    getBlogList,
    getBlogSortingFilters,
    toggleBlogStatus,
    deleteBlog,
} from '../../services/blogService';
import { BLOG_DEFAULT_SORT } from '../../constants/api.constants';

const initialState: BlogsState = {
    blogs: [],
    pagination: null,
    sortingOptions: [],
    statusOptions: [],
    togglingIds: [],
    deletingIds: [],
    loading: 'idle',
    error: null,
};

// ============================================
// Async Thunks
// ============================================

export const fetchBlogSortingFilters = createAsyncThunk(
    'blogs/fetchSortingFilters',
    async () => {
        return await getBlogSortingFilters();
    }
);

export const fetchBlogs = createAsyncThunk(
    'blogs/fetchList',
    async (params: BlogListParams = {}) => {
        const queryParams: BlogListParams = {
            page: params.page ?? 1,
            sortValue: params.sortValue ?? BLOG_DEFAULT_SORT,
            ...params,
        };
        return await getBlogList(queryParams);
    }
);

export const toggleBlogStatusThunk = createAsyncThunk(
    'blogs/toggleStatus',
    async (blogId: string) => {
        const result = await toggleBlogStatus(blogId);
        return { blogId, status: result.status };
    }
);

export const deleteBlogThunk = createAsyncThunk(
    'blogs/delete',
    async (blogId: string) => {
        await deleteBlog(blogId);
        return blogId;
    }
);

// ============================================
// Slice
// ============================================

const blogsSlice = createSlice({
    name: 'blogs',
    initialState,
    reducers: {
        clearBlogsError: (state) => {
            state.error = null;
        },
        resetBlogsState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            // Fetch sorting filters
            .addCase(fetchBlogSortingFilters.fulfilled, (state, action) => {
                state.sortingOptions = action.payload.sortingOptions;
                state.statusOptions = action.payload.statusOptions;
            })
            .addCase(fetchBlogSortingFilters.rejected, (state, action) => {
                state.error = action.error.message || 'Failed to fetch blog filters';
            })

            // Fetch blogs list
            .addCase(fetchBlogs.pending, (state) => {
                state.loading = 'loading';
                state.error = null;
            })
            .addCase(fetchBlogs.fulfilled, (state, action) => {
                state.loading = 'success';
                state.blogs = action.payload.blogs;
                state.pagination = action.payload.pagination;
                state.error = null;
            })
            .addCase(fetchBlogs.rejected, (state, action) => {
                state.loading = 'error';
                state.error = action.error.message || 'Failed to fetch blogs';
            })

            // Toggle blog status
            .addCase(toggleBlogStatusThunk.pending, (state, action) => {
                state.togglingIds.push(action.meta.arg);
            })
            .addCase(toggleBlogStatusThunk.fulfilled, (state, action) => {
                const { blogId, status } = action.payload;
                state.togglingIds = state.togglingIds.filter((id) => id !== blogId);
                const blog = state.blogs.find((b) => b._id === blogId);
                if (blog) {
                    blog.status = status as 'Active' | 'Inactive';
                }
            })
            .addCase(toggleBlogStatusThunk.rejected, (state, action) => {
                state.togglingIds = state.togglingIds.filter((id) => id !== action.meta.arg);
                state.error = action.error.message || 'Failed to toggle blog status';
            })

            // Delete blog
            .addCase(deleteBlogThunk.pending, (state, action) => {
                state.deletingIds.push(action.meta.arg);
            })
            .addCase(deleteBlogThunk.fulfilled, (state, action) => {
                const blogId = action.payload;
                state.deletingIds = state.deletingIds.filter((id) => id !== blogId);
                state.blogs = state.blogs.filter((b) => b._id !== blogId);
                if (state.pagination) {
                    state.pagination.totalCount = Math.max(0, state.pagination.totalCount - 1);
                }
            })
            .addCase(deleteBlogThunk.rejected, (state, action) => {
                state.deletingIds = state.deletingIds.filter((id) => id !== action.meta.arg);
                state.error = action.error.message || 'Failed to delete blog';
            });
    },
});

// ============================================
// Actions
// ============================================

export const { clearBlogsError, resetBlogsState } = blogsSlice.actions;

// ============================================
// Selectors (Custom Hooks)
// ============================================

export const useBlogsList = () => useSelector((state: RootState) => state.blogs.blogs);

export const useBlogsPagination = () =>
    useSelector((state: RootState) => state.blogs.pagination);

export const useBlogsSortingOptions = () =>
    useSelector((state: RootState) => state.blogs.sortingOptions);

export const useBlogsStatusOptions = () =>
    useSelector((state: RootState) => state.blogs.statusOptions);

export const useBlogsLoading = () => useSelector((state: RootState) => state.blogs.loading);

export const useBlogsError = () => useSelector((state: RootState) => state.blogs.error);

export const useBlogsTogglingIds = () =>
    useSelector((state: RootState) => state.blogs.togglingIds);

export const useBlogsDeletingIds = () =>
    useSelector((state: RootState) => state.blogs.deletingIds);

// ============================================
// Reducer
// ============================================

export default blogsSlice.reducer;
